/* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-argument */
import { Injectable } from '@angular/core'
import { map } from 'rxjs/operators'
import { Observable, Subject } from 'rxjs'
import { Dayjs } from 'dayjs'

import { ApiService } from '@shared/services/api.service'
import { FeatureFlagService } from '@app/core/services/feature-flag.service'
import { withFeedback } from '@app/core/interceptors/feedback-context'
import {
  CameraDevice,
  Device,
  DeviceContract,
  DeviceCoordinates,
  DeviceInstallation,
  DeviceInstallUninstallInputData,
  DeviceReplacement,
  DeviceContractUpdateData,
  DeviceHistory,
  CamerasEventsType,
  CamerasEvent,
} from '@shared/models/device.model'
import { DeviceVendor } from '@shared/models/device-vendor.model'
import { RawDataLog } from '@shared/models/raw-data-log.model'
import { NoteHistory } from '@shared/models/note.model'
import { DeviceAssignmentsImport, DeviceImport } from './shared/device-import.model'
import { toFormData } from '@core/helpers/to-form-data'
import { History, TeamHistory } from '@shared/models/history.model'
import { Sensor } from '@shared/models/sensor.model'
import { FilterValues } from '@shared/models/filter-values.model'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'
import { PagesData } from '@shared/models/pages-data.model'
import { DeviceCoordinateFilter } from '@shared/models/coordinate.model'

@Injectable({ providedIn: 'root' })
export class DevicesService {
  // Emits the updated device after a successful save from device-form.
  // Parents (e.g. devices-details) subscribe to refresh their header without re-fetching from BE.
  private readonly deviceUpdatedSource = new Subject<Device>()
  readonly deviceUpdated$ = this.deviceUpdatedSource.asObservable()

  constructor(
    private apiService: ApiService,
    private featureFlagService: FeatureFlagService,
  ) {}

  notifyDeviceUpdated(device: Device): void {
    this.deviceUpdatedSource.next(device)
  }

  getDevices(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: { [param: string]: string | number | (string | number)[] },
    fields?: (keyof Device | string)[],
  ): Observable<PagesData<Device>> {
    const toDevice = (device: Device) => new Device(device)

    return this.apiService
      .getPageable<Device>('/devices/json', pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ data: data.map(toDevice), ...rest })))
  }

  getDeviceList(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: { [param: string]: string | number | (string | number)[] },
    fields?: (keyof Device)[],
  ) {
    return this.getDevices(pageIndex, pageSize, sort, filterValues, fields).pipe(map((devices) => devices.data))
  }

  getResellerDevices(
    resellerId: number | string,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: { [param: string]: string | number | (string | number)[] },
    fields?: (keyof Device)[],
  ): Observable<PagesData<Device>> {
    const toDevice = (device: Device) => new Device(device)

    return this.apiService
      .getPageable<Device>(`/reseller/${resellerId}/devices/json`, pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ data: data.map(toDevice), ...rest })))
  }

  // TODO: update dateFrom and dateTo type after API update
  getDeviceCoordinates({
    deviceId,
    dateFrom,
    dateTo,
    sameTimeEachDay,
    filter,
  }: {
    deviceId: string
    dateFrom: Dayjs
    dateTo: Dayjs
    sameTimeEachDay?: boolean
    filter: DeviceCoordinateFilter
  }): Observable<{ data: DeviceCoordinates[] }> {
    const params = {
      dateFrom: dateFrom.format(),
      dateTo: dateTo.format(),
      ...(sameTimeEachDay !== undefined && { sameTimeEachDay }),
      filter,
    }
    return this.apiService
      .get(`/devices/${deviceId}/coordinates`, { params })
      .pipe(map((resp) => ({ data: resp['data'].map((status) => new DeviceCoordinates(status)) })))
  }

  getDevice(id: string, fields?: (keyof Device)[]) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const params = {
      ...(fields && { 'fields[]': fields }),
    }
    return this.apiService.get(`/devices/${id}`, { params }).pipe(map((resp) => new Device(resp['data'])))
  }

  getVendors() {
    const path = this.featureFlagService.isGo ? '/devices/vendors' : '/devices/vendors/'
    return this.apiService.get(path).pipe(map((resp) => resp['data'].map((vendor) => new DeviceVendor(vendor))))
  }

  addDevice(data: Partial<Device>) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any
    return this.apiService.post(`/devices`, data, { context: withFeedback() }).pipe(map((device: any) => new Device(device)))
  }

  getDeviceInstallation(deviceImei?: string, vehicleRegNo?: string) {
    const params = {
      ...(deviceImei && { deviceImei }),
      ...(vehicleRegNo && { vehicleRegNo }),
    }
    return this.apiService.get('/devices/installation/', { params }).pipe(
      map((resp) => {
        const deviceInstallation = resp['data']
        return deviceInstallation && deviceInstallation.id ? new DeviceInstallation(deviceInstallation) : null
      }),
    )
  }

  installDevice(deviceId: string, data: DeviceInstallUninstallInputData): Observable<Device> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any
    return this.apiService.post(`/devices/${deviceId}/install`, data, { context: withFeedback() }).pipe(map((data) => data)) as any
  }

  uninstallDevice(deviceId: string, data: DeviceInstallUninstallInputData): Observable<Device> {
    return this.apiService.post(`/devices/${deviceId}/uninstall`, data, { context: withFeedback() })
  }

  updateDevice(id: string, data: Partial<Device>, withToast = true) {
    // withToast=false for bulk callers (vd move-devices) that loop over many devices and report
    // their own per-row status — avoids a toast storm. Single-save callers keep the default toast.
    const options = withToast ? { context: withFeedback() } : undefined
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-explicit-any
    return this.apiService.patch(`/devices/${id}`, data, options).pipe(map((device: any) => new Device(device)))
  }

  getLogs(pageIndex = 0, pageSize = 10, sort?: string, filterValues?: { [param: string]: string | number | (string | number)[] }) {
    const toRawDataLog = (log) => new RawDataLog(log)
    return this.apiService
      .getPageable<RawDataLog>('/devices/logs/json', pageIndex, pageSize, sort, filterValues)
      .pipe(map((data) => ({ data: data.data.map(toRawDataLog), meta: data.meta })))
  }

  exportLogsFile(columns: string[], sort: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return this.apiService.exportFile(`/devices/logs/${type}`, columns, sort, filterValues)
  }

  exportResellerFile(
    resellerId: number | string,
    columns: string[],
    sort: string,
    filterValues: FilterValues,
    type: ExportFileTypes,
  ): Observable<string> {
    return this.apiService.exportFile(`/reseller/${resellerId}/devices/${type}`, columns, sort, filterValues)
  }

  exportFile(columns: string[], sort: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return this.apiService.exportFile(`/devices/${type}`, columns, sort, filterValues)
  }

  getAdminNotesHistory(deviceId: number): Observable<NoteHistory[]> {
    return this.apiService.get(`/device-notes/${deviceId}/admin`).pipe(map((resp) => resp['data'].map((note) => new NoteHistory(note))))
  }

  getClientNotesHistory(deviceId: number): Observable<NoteHistory[]> {
    return this.apiService.get(`/device-notes/${deviceId}/client`).pipe(map((resp) => resp['data'].map((note) => new NoteHistory(note))))
  }

  addDeviceNote(id: number | string, note: string, noteType: 'admin' | 'client'): Observable<void> {
    const body = {
      [`${noteType}Note`]: note,
    }

    return this.apiService.post(`/devices/${id}/notes`, body, { context: withFeedback() })
  }

  getDeviceStatusHistory(deviceId: string, pageIndex = 1, pageSize = 10): Observable<PagesData<History>> {
    return this.apiService
      .getPageable<History>(`/device-status/${deviceId}`, pageIndex, pageSize)
      .pipe(map((data) => ({ data: data['data'], meta: data.meta })))
  }

  // NOTE: upload-as-fetch — POSTs the file and RETURNS parsed preview rows for the confirm screen;
  // the actual commit is saveFile(). Do NOT opt in to FormFeedbackInterceptor (withFeedback):
  // a "Saved" toast here would fire before the user confirms the import.
  importFile(file: FileList): Observable<DeviceImport> {
    const formData = toFormData({ files: [file] })
    return this.apiService.post('/devices-vehicles/upload', formData)
  }

  // NOTE: upload-as-fetch — POSTs the file and RETURNS parsed preview rows for the confirm screen;
  // the actual commit happens on the dialog's save step. Do NOT opt in to FormFeedbackInterceptor
  // (withFeedback): a "Saved" toast here would fire before the user confirms the import.
  importDeviceAssignFile(file: FileList): Observable<DeviceAssignmentsImport> {
    const formData = toFormData({ files: file })
    return this.apiService.post('/device-assign/upload', formData)
  }

  saveFile(fileId: string | number): Observable<DeviceImport> {
    const formData = toFormData({ 'files[]': fileId })
    return this.apiService.post(`/devices-vehicles/save`, formData, { context: withFeedback() })
  }

  linkDeviceToSensor(sensorId: string, data: Pick<Device, 'deviceId'>): Observable<Sensor> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return this.apiService.post(`/sensors/${sensorId}/install`, data, { context: withFeedback() }).pipe(map((response) => response['data']))
  }

  getDeviceContractHistory(
    deviceId: string | number,
    deviceContract: DeviceContract,
    pageIndex = 0,
    pageSize = 10,
  ): Observable<PagesData<History>> {
    return this.apiService.getPageable(`/device/${deviceId}/history/${deviceContract.toString()}`, pageIndex, pageSize)
  }

  getDeviceContractHistoryList(
    deviceId: string | number,
    deviceContract: DeviceContract,
    pageIndex = 1,
    pageSize = 1000,
  ): Observable<History[]> {
    return this.getDeviceContractHistory(deviceId, deviceContract, pageIndex, pageSize).pipe(map((res) => res['data']))
  }

  getDeviceTeamHistory(deviceId: string | number): Observable<TeamHistory[]> {
    return this.apiService.get(`/device/${deviceId}/team-history`).pipe(map((response) => response['data']))
  }

  getReplacements(pageIndex = 0, pageSize = 10, sort?: string, filterValues?: FilterValues): Observable<PagesData<DeviceReplacement>> {
    return this.apiService.getPageable('/devices/replacements', pageIndex, pageSize, sort, filterValues)
  }

  replaceDevice(oldDeviceId: string, newDeviceId: string, reason: string): Observable<DeviceReplacement> {
    return this.apiService.post(`/devices/${oldDeviceId}/replacements/${newDeviceId}`, { reason }, { context: withFeedback() })
  }

  deleteReplacement(replacementId: number): Observable<void> {
    return this.apiService.delete(`/devices/replacements/${replacementId}`, { context: withFeedback() })
  }

  patchReplacement(replacementId: number, data: Partial<DeviceReplacement>): Observable<void> {
    return this.apiService.patch(`/devices/replacements/${replacementId}`, data, { context: withFeedback() })
  }

  correctInstallationDate(
    deviceId: string | number,
    installationId: string | number,
    data: Pick<DeviceInstallation, 'installDate'>,
  ): Observable<Device> {
    return this.apiService
      .post(`/devices/${deviceId}/installation/${installationId}`, data, { context: withFeedback() })
      .pipe(map((response) => response['data']))
  }

  getDeviceHistory(deviceId: string | number): Observable<DeviceHistory[]> {
    return this.apiService.get(
      `/devices/${deviceId}/history?type[]=device.fields&type[]=device.contract.changed&type[]=device.installed&type[]=device.uninstalled`,
    )
  }

  getCameras(deviceId: string | number): Observable<CameraDevice[]> {
    return this.apiService.get(`/devices/${deviceId}/cameras`)
    // .pipe(map((response) => response['data']))
  }

  getCamerasEventsTypes(): Observable<CamerasEventsType[]> {
    return this.apiService.get('/devices/cameras/events/types').pipe(map((response) => response['data']))
  }

  getCamerasEvents(params: { [key: string]: number | string }): Observable<PagesData<CamerasEvent>> {
    const filteredParams = Object.fromEntries(Object.entries(params).filter(([_, value]) => !!value))
    return this.apiService.get('/devices/cameras/events', { params: filteredParams })
  }

  updateDevicesContract(deviceId: string[], data: DeviceContractUpdateData): Observable<void> {
    return this.apiService.post('/devices/bulk/contract', { deviceId, ...data }, { context: withFeedback() })
  }

  updateDevicesOwnership(deviceId: string[], data: { ownership: string }): Observable<void> {
    return this.apiService.post('/devices/bulk/ownership', { deviceId, ...data }, { context: withFeedback() })
  }
}
